import styled from "styled-components";
import { useState, useRef, useEffect } from "react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useNavigate } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import { preloadImage } from "modules/functions";
import InputText from "components/forms/InputText";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import Tip from "components/other/Tip";
import DropdownSelection from "components/forms/DropdownSelection";
import SelectionItem from "components/forms/SelectionItem";
import Dropdown from "components/forms/Dropdown";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";

import copyIcon from "assets/img/icons/copy-icon.svg";
import copyWhiteIcon from "assets/img/icons/copy-white-icon.svg";
import checkIcon from "assets/img/icons/icons8-check.svg";
import checkWhiteIcon from "assets/img/icons/icons8-check-white.svg";

const ReferralLinkBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const BoxTop = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
`;

const BoxTopLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};

  flex: 1;
`;

const BoxTitle = styled.div`
  width: 100%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};

  display: flex;
  align-items: center;
  gap: 6px;
`;

const Text = styled.div``;

const InputWrap = styled.div`
  position: relative;
  max-width: 400px;
`;

const CopyIconBox = styled.div`
  position: absolute;
  top: 50%;
  right: 15px;

  transform: translateY(-50%);
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const PageSelectionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PageSelectedText = styled.div``;

const PageSelectionText = styled.div`
  font-family: "Arial", sans-serif;
`;

const ButtonWrap = styled.div`
  display: flex;
`;

export default ({ dashboard }) => {
  preloadImage(copyWhiteIcon);
  preloadImage(checkIcon);
  preloadImage(checkWhiteIcon);

  const pageDropdownRef = useRef();
  const pageSelectionRef = useRef();
  const navigate = useNavigate();

  const [copied, setCopied] = useState(false);
  const [pageSelectionOpened, setPageSelectionOpened] = useState(false);
  const [pageSelected, setPageSelected] = useState(null);

  const UserProperties = createSelector(
    (state) => state.User,
    (user) => ({
      profileData: user.profileData,
    })
  );

  const { profileData } = useSelector(UserProperties);

  const linkCopied = () => {
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  // handle Page selection
  const togglePageSelection = () => {
    setPageSelectionOpened((prevState) => !prevState);
  };

  const onPageSelect = (page) => {
    setPageSelected(page);
    setPageSelectionOpened(false);
  };

  function usePageSelectionOutsideClick(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !pageDropdownRef.current.contains(event.target)
        ) {
          togglePageSelection();
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  usePageSelectionOutsideClick(pageSelectionRef);

  const pageOptions = {
    homepage: {
      name: "Scalelup Homepage",
      url: "https://scalelup.com",
    },
    shortLanding: {
      name: "Short Landing Page",
      url: "https://scalelup.com/promo-one",
    },
    mlmLanding: {
      name: "MLM Landing Page",
      url: "https://scalelup.com/promo-mlm",
    },
    earnLanding: {
      name: "Earn Landing Page",
      url: "https://scalelup.com/promo-earn",
    },
  };

  let pageOptionsList = [];

  Object.keys(pageOptions).forEach((page) => {
    pageOptionsList.push(
      <SelectionItem
        onClick={() => onPageSelect(page)}
        key={pageOptions[page].name}
      >
        <PageSelectionItem>
          <PageSelectionText>{pageOptions[page].name}</PageSelectionText>
        </PageSelectionItem>
      </SelectionItem>
    );
  });

  const referralLink = pageSelected
    ? `${pageOptions[pageSelected].url}?r=${profileData.referralCode}`
    : `${pageOptions.homepage.url}?r=${profileData.referralCode}`;

  return (
    <ReferralLinkBox>
      <BoxTop>
        <BoxTopLeft>
          <BoxTitle>
            Share Your Link
            <Tip
              tipId="referralLink"
              tip="Users who register with your link will become your referrals. You can choose which landing page visitors will be directed to, based on your target audience."
            />
          </BoxTitle>
          <Text>Use this link to refer new users to Scalelup.</Text>
          <DropdownWrap>
            <Dropdown
              onClick={togglePageSelection}
              dropdownRef={pageDropdownRef}
            >
              {!pageSelected && "Choose landing page"}
              {pageSelected && (
                <PageSelectionItem>
                  <PageSelectedText>
                    {pageOptions[pageSelected].name}
                  </PageSelectedText>
                </PageSelectionItem>
              )}
            </Dropdown>
            {pageSelectionOpened && (
              <DropdownSelection
                width="290px"
                top="48px"
                selectionRef={pageSelectionRef}
                maxHeight="220px"
              >
                {pageOptionsList}
              </DropdownSelection>
            )}
          </DropdownWrap>
          <InputWrap>
            <InputText
              type="text"
              value={referralLink}
              readOnly={true}
              righticonspace={true}
            />
            <CopyIconBox>
              <CopyToClipboard text={referralLink} onCopy={linkCopied}>
                <ButtonRoundIcon
                  icon={copied ? checkIcon : copyIcon}
                  hoverIcon={copied ? checkWhiteIcon : copyWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  buttonSize="24px"
                  iconSize="10px"
                />
              </CopyToClipboard>
            </CopyIconBox>
          </InputWrap>
        </BoxTopLeft>
      </BoxTop>
      {dashboard && (
        <ButtonWrap>
          <ButtonPrimarySmall onClick={() => navigate("/referrals")}>
            Referral Program
          </ButtonPrimarySmall>
        </ButtonWrap>
      )}
    </ReferralLinkBox>
  );
};
